import { styled } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import Toolbar from '@mui/material/Toolbar';
import FooterLayout from 'layouts/FooterLayout';
import Logo from 'logos/Logo';
import LocaleButton from 'components/LocaleButton';
import { Box, LinearProgress } from '@mui/material';
import { useMsal } from '@azure/msal-react';
import { getLoginRequest } from 'auth';
import { defaultHomePage } from 'auth/msalReactAuthProvider';
import I18nLabel from './components/i18nLabel';
import { useEffect, useState } from 'react';

const PREFIX = 'BasicLayout';

const classes = {
  root: `${PREFIX}-root`,
  toolbar: `${PREFIX}-toolbar`,
  toolbarButtons: `${PREFIX}-toolbarButtons`,
  red: `${PREFIX}-red`,
  green: `${PREFIX}-green`,
  appBar: `${PREFIX}-appBar`,
  appBarSpacer: `${PREFIX}-appBarSpacer`,
  contentContainer: `${PREFIX}-contentContainer`,
  content: `${PREFIX}-content`,
  title1: `${PREFIX}-title1`,
  imageContainer: `${PREFIX}-imageContainer`,
  title2: `${PREFIX}-title2`,
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.root}`]: {
    position: 'relative',
    marginLeft: 'auto',
    marginRight: 'auto',
    display: 'flex',
  },

  [`& .${classes.toolbar}`]: {
    display: 'flex',
    '& > *': {
      flex: 1,
    },
  },

  [`& .${classes.toolbarButtons}`]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    display: 'flex',
    justifyContent: 'flex-end',
    '& > *': {
      marginLeft: theme.spacing(2),
    },
  },

  [`& .${classes.red}`]: {
    backgroundColor: red,
  },

  [`& .${classes.green}`]: {
    backgroundColor: green,
  },

  [`& .${classes.appBar}`]: {
    zIndex: theme.zIndex.drawer + 1,
  },

  [`& .${classes.appBarSpacer}`]: {
    minHeight: 70,
  },

  [`& .${classes.contentContainer}`]: {
    height: '100vh',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
  },

  [`& .${classes.content}`]: {
    textAlign: 'center',
    margin: 'auto',
    maxWidth: 1600,
    paddingBottom: 50,
  },

  [`& .${classes.title1}`]: {
    marginLeft: 40,
    fontWeight: 'bolder',
    margin: 0,
    lineHeight: 1.1,
    fontSize: 45,
    color: 'white',
    [theme.breakpoints.down('xs')]: {
      fontSize: 25,
    },
  },

  [`& .${classes.imageContainer}`]: {
    [theme.breakpoints.up('sm')]: {
      minWidth: 600,
      maxWidth: 800,
    },
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },

  [`& .${classes.title2}`]: {
    fontSize: 18,
    lineHeight: 1,
    color: 'white',
    textTransform: 'uppercase',
  },
}));

const red = '#FF75A8';
const green = '#26C9A8';

interface IProps {
  brandingGetter?: () => Promise<string>;
  children: JSX.Element;
}

export default function BasicLayout(props: IProps): JSX.Element {
  const { brandingGetter } = props;
  const [loading, setLoading] = useState(false);
  const [branding, setBranding] = useState('');
  const { instance } = useMsal();

  const login = async () => {
    await instance.loginRedirect(getLoginRequest(defaultHomePage));
  };

  useEffect(() => {
    if (brandingGetter) {
      const fetchBranding = async () => {
        setLoading(true);
        try {
          const branding = await brandingGetter();
          setBranding(branding);
        } catch (e) {
          console.error(e);
        }
        setLoading(false);
      };
      fetchBranding();
    }
  }, [brandingGetter]);

  if (loading) {
    return <LinearProgress color="inherit" />;
  }

  return (
    <Root className={classes.root}>
      <CssBaseline />
      <AppBar position="absolute" className={classes.appBar}>
        <Toolbar className={classes.toolbar}>
          <div>
            <Logo src={branding} color="white" height={40} />
          </div>
          <div className={classes.toolbarButtons}>
            <LocaleButton />
            {/* <ColorButton variant="contained" color="primary" className={classes.red} > Subscribe </ColorButton> */}
            <Button variant="contained" color="secondary" onClick={login}>
              <I18nLabel labelId="login" />
            </Button>
          </div>
        </Toolbar>
      </AppBar>
      <main className={classes.contentContainer}>
        <div className={classes.appBarSpacer} />
        <Box sx={{ padding: 6, margin: 10, flex: 1 }}>
          <div className="form-container flex flex-col justify-center">{props.children}</div>
        </Box>
        <FooterLayout />
      </main>
    </Root>
  );
}
